import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { getCategory, categories } from "../mock";
import { getArticlesByCategory } from "../articlesData";
import { ArrowRight, ArrowLeft, Heart, MessageCircle } from "lucide-react";
import BookmarkButton from "../components/BookmarkButton";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Manually curated "Most loved" picks per topic (overrides heart-ranking)
const CURATED_FEATURED = {
  career: [
    "put-yourself-out-there-start-that-thing",
    "the-worst-they-can-say-is-no",
    "fail-again-again-and-again",
  ],
};

const TopicPage = () => {
  const { slug } = useParams();
  const category = getCategory(slug);
  const posts = getArticlesByCategory(slug || "");
  const [counts, setCounts] = useState({});

  useEffect(() => {
    const slugs = posts.map((p) => p.slug).join(",");
    if (!slugs) return;
    axios
      .get(`${API}/articles/meta-batch?slugs=${encodeURIComponent(slugs)}`)
      .then((res) => setCounts(res.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (!category) {
    return (
      <div className="bg-[#eceae5] min-h-screen">
        <Navbar solid />
        <div className="pt-44 pb-40 text-center">
          <h1 className="font-display text-4xl text-neutral-900">Topic not found</h1>
          <Link to="/" className="font-sans-ui text-neutral-600 underline mt-4 inline-block">Back home</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const nice = category.name.charAt(0) + category.name.slice(1).toLowerCase();
  const curated = CURATED_FEATURED[(slug || "").toLowerCase()];
  const ranked = curated
    ? curated
        .map((s) => posts.find((p) => p.slug === s))
        .filter(Boolean)
        .map((p) => ({ ...p, hearts: (counts[p.slug] && counts[p.slug].reactions) || 0 }))
    : [...posts]
        .map((p, i) => ({ p, i, hearts: (counts[p.slug] && counts[p.slug].reactions) || 0 }))
        .sort((a, b) => b.hearts - a.hearts || a.i - b.i)
        .slice(0, 3)
        .map((r) => ({ ...r.p, hearts: r.hearts }));
  const showLoved = posts.length > 3;

  return (
    <div className="bg-[#eceae5] min-h-screen">
      <SEO title={nice} description={category.tagline} image={category.image} type="website" />
      <Navbar solid />

      {/* Banner */}
      <header className="relative h-[62vh] min-h-[460px] w-full overflow-hidden">
        <img
          src={category.image}
          alt={category.name}
          className="absolute inset-0 w-full h-full object-cover topic-hero-zoom"
          style={{ objectPosition: category.imagePosition || "center" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/45 to-black/35" />
        <div className="relative h-full max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-14 md:pb-16">
          <Link
            to="/topics"
            className="inline-flex items-center gap-2 self-start font-sans-ui text-xs uppercase tracking-[0.2em] text-white/80 hover:text-white border border-white/30 hover:border-white/70 rounded-full px-4 py-2 mb-6 transition-colors"
            data-testid="topic-back-to-topics"
          >
            <ArrowLeft size={14} /> All topics
          </Link>
          <h1
            className="font-display text-white text-5xl sm:text-6xl md:text-8xl leading-[0.95] tracking-tight"
            style={{ fontWeight: 700 }}
          >
            {category.name.charAt(0) + category.name.slice(1).toLowerCase()}
          </h1>
          <p className="font-display italic text-white/85 text-lg md:text-2xl mt-5 max-w-xl">
            {category.tagline}
          </p>
          <p className="font-sans-ui text-white/70 text-xs uppercase tracking-[0.2em] mt-6">
            {posts.length} {posts.length === 1 ? "story" : "stories"}
          </p>
        </div>
      </header>

      {/* Most loved in category */}
      {showLoved && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-14 md:pt-20" data-testid="topic-most-loved">
          <div className="mb-8">
            <p className="font-sans-ui uppercase tracking-[0.25em] text-sm text-neutral-500">Start here</p>
            <h2 className="font-display text-3xl md:text-4xl text-neutral-900 mt-2 leading-none tracking-tight" style={{ fontWeight: 700 }}>
              Most loved in {nice}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-7">
            {ranked.map((post, idx) => (
              <Link key={post.slug} to={`/article/${post.slug}`} className="group" data-testid={`topic-most-loved-${post.slug}`}>
                <div className="relative w-full aspect-[16/10] overflow-hidden rounded-lg">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover transition-transform duration-[1100ms] ease-out group-hover:scale-105" />
                  <span className="absolute top-3 left-3 font-display text-white text-base w-8 h-8 rounded-full bg-neutral-900/70 backdrop-blur-sm flex items-center justify-center" style={{ fontWeight: 700 }}>
                    {idx + 1}
                  </span>
                  {post.hearts > 0 && (
                    <span className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 font-sans-ui text-xs text-white bg-neutral-900/70 backdrop-blur-sm px-2.5 py-1 rounded-full">
                      <Heart size={12} fill="currentColor" /> {post.hearts}
                    </span>
                  )}
                </div>
                <h3 className="font-display text-lg md:text-xl text-neutral-900 mt-3 leading-snug group-hover:opacity-70 transition-opacity" style={{ fontWeight: 700 }}>
                  {post.title}
                </h3>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Articles */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-16">
        {showLoved && (
          <p className="font-sans-ui uppercase tracking-[0.2em] text-sm text-neutral-500 mb-8">All {nice} stories</p>
        )}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
          {posts.map((post) => (
            <Link key={post.slug} to={`/article/${post.slug}`} className="group">
              <div className="relative w-full aspect-[4/3] overflow-hidden rounded-md">
                <BookmarkButton type="article" id={post.slug} />
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                />
              </div>
              <p className="font-sans-ui text-xs uppercase tracking-widest text-neutral-500 mt-4">
                {post.date} &middot; {post.readTime}
              </p>
              <h3 className="font-display text-2xl text-neutral-900 mt-2 leading-snug group-hover:opacity-70 transition-opacity" style={{ fontWeight: 700 }}>
                {post.title}
              </h3>
              <p className="font-display text-neutral-600 mt-2 leading-relaxed">{post.excerpt}</p>
              <div className="flex items-center gap-4 mt-3">
                <span className="inline-flex items-center gap-1.5 font-sans-ui text-sm text-neutral-900">
                  Read <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                </span>
                <span className="inline-flex items-center gap-1.5 font-sans-ui text-sm text-neutral-500" data-testid={`card-likes-${post.slug}`}>
                  <Heart size={14} /> {counts[post.slug] ? counts[post.slug].reactions : 0}
                </span>
                <span className="inline-flex items-center gap-1.5 font-sans-ui text-sm text-neutral-500" data-testid={`card-comments-${post.slug}`}>
                  <MessageCircle size={14} /> {counts[post.slug] ? counts[post.slug].comments : 0}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Other topics */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24 pt-8 border-t border-neutral-300">
        <p className="font-sans-ui uppercase tracking-[0.2em] text-sm text-neutral-500 mb-6">Explore other topics</p>
        <div className="flex flex-wrap gap-3">
          {categories.filter((c) => c.slug !== category.slug).map((c) => (
            <Link
              key={c.slug}
              to={`/topics/${c.slug}`}
              className="font-display text-2xl text-neutral-900 px-6 py-3 rounded-full border border-neutral-300 hover:bg-neutral-900 hover:text-white transition-colors"
              style={{ fontWeight: 700 }}
            >
              {c.name.charAt(0) + c.name.slice(1).toLowerCase()}
            </Link>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TopicPage;
